import {
  WidgetContentType,
  WidgetDisplayLogic,
  WidgetLayoutType,
  WidgetStatus
} from '@/common/model/recommend/widget'

import { BadgeType } from '@/components/view/atoms/badge/Badge'

// レコメンドパーツ一覧の検索条件（空文字＝すべて）
export interface WidgetSearchCondition {
  contentType: '' | WidgetContentType
  displayLogic: '' | WidgetDisplayLogic
}

export const WIDGET_SEARCH_CONDITION_CLEAR: WidgetSearchCondition = {
  contentType: '',
  displayLogic: ''
}

export const widgetStatusName = (status: WidgetStatus) => {
  switch (status) {
    case WidgetStatus.ACTIVE:
      return '利用中'
    case WidgetStatus.INACTIVE:
      return '利用しない'
    case WidgetStatus.ARCHIVED:
      return '廃止'
  }
}

export const WIDGET_STATUS_CHOICES: { name: string; value: WidgetStatus; badgeType: BadgeType }[] =
  [
    {
      name: widgetStatusName(WidgetStatus.ACTIVE),
      value: WidgetStatus.ACTIVE,
      badgeType: BadgeType.SUCCESS
    },
    {
      name: widgetStatusName(WidgetStatus.INACTIVE),
      value: WidgetStatus.INACTIVE,
      badgeType: BadgeType.DEFAULT
    },
    {
      name: widgetStatusName(WidgetStatus.ARCHIVED),
      value: WidgetStatus.ARCHIVED,
      badgeType: BadgeType.SECONDARY
    }
  ]

export const widgetDisplayLogicName = (displayLogic: WidgetDisplayLogic) => {
  switch (displayLogic) {
    case WidgetDisplayLogic.BB:
      return 'BB - この商品を買った人はこちらも購入'
    case WidgetDisplayLogic.OB:
      return 'OB - この商品を見た人はこちらを購入'
    case WidgetDisplayLogic.PERSONAL:
      return 'Personal - パーソナライズドレコメンド'
    case WidgetDisplayLogic.SIMILAR:
      return 'Similar - 類似商品'
    case WidgetDisplayLogic.MAIL:
      return 'Mail - メール配信用レコメンド'
    case WidgetDisplayLogic.ADAPTIVE:
      return 'Adaptive - 状況適応型レコメンド'
    case WidgetDisplayLogic.RANKING_BLEND:
      return 'Ranking Blend - ランキング×パーソナル'
    case WidgetDisplayLogic.RECENTLY_VIEWED:
      return 'Recently Viewed - 最近見たものの続き'
    case WidgetDisplayLogic.RELATED_BY_ARTICLE:
      return 'Related By Article - この特集に関連する商品'
    case WidgetDisplayLogic.PERSONAL_BY_ARTICLE:
      return 'Personal By Article - この特集を見た人はこちらも見ています'
    case WidgetDisplayLogic.ARTICLE_TRENDING:
      return 'Article Trending - 人気の記事'
    case WidgetDisplayLogic.ARTICLE_SIMILAR:
      return 'Article Similar - 関連する記事'
    case WidgetDisplayLogic.ARTICLE_PERSONAL:
      return 'Article Personal - この記事を読んだ人はこちらも'
    case WidgetDisplayLogic.ARTICLE_RELATED_BY_ITEM:
      return 'Article Related By Item - この商品に関連する記事'
  }
}

export const widgetDisplayLogicContentType = (
  displayLogic: WidgetDisplayLogic
): 'product' | 'article' => {
  return (
    [
      WidgetDisplayLogic.BB,
      WidgetDisplayLogic.OB,
      WidgetDisplayLogic.PERSONAL,
      WidgetDisplayLogic.SIMILAR,
      WidgetDisplayLogic.ADAPTIVE,
      WidgetDisplayLogic.RANKING_BLEND,
      WidgetDisplayLogic.RECENTLY_VIEWED,
      WidgetDisplayLogic.RELATED_BY_ARTICLE,
      WidgetDisplayLogic.PERSONAL_BY_ARTICLE,
      WidgetDisplayLogic.MAIL
    ] as WidgetDisplayLogic[]
  ).includes(displayLogic)
    ? 'product'
    : 'article'
}

// 検索条件・選択肢の単一ソース（全ロジック）。バックエンド DISPLAY_LOGIC_LABELS と値を一致させる。
export const WIDGET_DISPLAY_LOGIC_CHOICES: { name: string; value: WidgetDisplayLogic }[] =
  Object.values(WidgetDisplayLogic).map((v) => ({ name: widgetDisplayLogicName(v), value: v }))

export const widgetLayoutTypeName = (layoutType: WidgetLayoutType) => {
  switch (layoutType) {
    case WidgetLayoutType.GRID:
      return 'グリッド'
    case WidgetLayoutType.CAROUSEL:
      return 'カルーセル'
    case WidgetLayoutType.SLIDER:
      return 'スライダー'
    case WidgetLayoutType.LIST:
      return 'リスト'
    case WidgetLayoutType.BUNDLE:
      return 'バンドル（まとめてカートに入れる）'
  }
}

// ---- バンドル（まとめてカートに入れる）レイアウト ----
// 条件はバックエンドの検証（routers/widgets.py）と一致させる。

// バンドルを選べるレコメンドロジック（商品IDを起点にする商品ロジック）
export const BUNDLE_LAYOUT_LOGICS: WidgetDisplayLogic[] = [
  WidgetDisplayLogic.BB,
  WidgetDisplayLogic.OB,
  WidgetDisplayLogic.SIMILAR
]

// バンドルの表示数（主商品に加えて並べる併売候補の数）
export const BUNDLE_MAX_ITEMS_MIN = 1
export const BUNDLE_MAX_ITEMS_MAX = 3
export const BUNDLE_MAX_ITEMS_CHOICES = [1, 2, 3].map((v) => ({
  label: v.toString(),
  value: v.toString()
}))

/**
 * バンドルを選べない理由を返す。選べる場合は null。
 */
export const bundleLayoutUnavailableReason = (
  contentType: WidgetContentType,
  displayLogic: WidgetDisplayLogic
): string | null => {
  if (contentType !== WidgetContentType.PRODUCT) {
    return 'バンドルは商品レコメンドでのみ選べます。コンテンツタイプを変更するか、別のレイアウトを選んでください。'
  }
  if (!BUNDLE_LAYOUT_LOGICS.includes(displayLogic)) {
    return 'バンドルは「BB」「OB」「Similar」のロジックでのみ選べます。ロジックを変更するか、別のレイアウトを選んでください。'
  }
  return null
}

// バンドルでカスタムCSSに使えるクラス名（配信JSの描画と一致させる）
export const BUNDLE_CSS_CLASSES: { className: string; description: string }[] = [
  { className: 'w2sr-layout-bundle', description: '枠全体' },
  { className: 'w2sr-widget-title', description: '見出し（表示レコメンドスタイルの文言）' },
  { className: 'w2sr-bundle', description: '画像・合計・商品一覧をまとめる領域' },
  { className: 'w2sr-bundle-visual', description: '画像の並び' },
  { className: 'w2sr-bundle-thumb', description: '商品画像の枠（チェックを含む）' },
  { className: 'w2sr-bundle-img', description: '商品画像' },
  { className: 'w2sr-bundle-check', description: 'カートに入れるかのチェック' },
  { className: 'w2sr-bundle-plus', description: '画像の間の「＋」' },
  { className: 'w2sr-bundle-summary', description: '合計金額とボタンのまとまり' },
  { className: 'w2sr-bundle-total', description: '合計金額の行' },
  { className: 'w2sr-bundle-total-price', description: '合計金額の数字' },
  { className: 'w2sr-bundle-count', description: '選択中の点数' },
  { className: 'w2sr-bundle-cart', description: 'まとめてカートに入れるボタン' },
  { className: 'w2sr-bundle-note', description: '合計の下の注記' },
  { className: 'w2sr-bundle-list', description: '商品名の一覧' },
  { className: 'w2sr-bundle-item', description: '商品1件の行' },
  { className: 'w2sr-bundle-main', description: '主商品（画像の枠と行の両方に付く）' },
  { className: 'w2sr-bundle-label', description: '主商品の行の「この商品」' },
  { className: 'w2sr-bundle-name', description: '商品名（併売候補は商品ページへのリンク）' },
  { className: 'w2sr-bundle-price', description: '商品の価格' },
  { className: 'w2sr-bundle-variation', description: 'バリエーションの選択欄' },
  { className: 'is-off', description: 'チェックを外した商品に付く状態のクラス' }
]

// 埋め込みコード一覧（表示コード）のクラス名一覧へのアンカー
export const BUNDLE_CSS_CLASSES_ANCHOR = 'bundle-css-classes'

export const widgetPatternColorClass = (index: number, abVariantCount: number = 999) => {
  return index >= abVariantCount
    ? 'text-placeholder'
    : index === 0
      ? 'text-[#006B99]'
      : index === 1
        ? 'text-success'
        : index === 2
          ? 'text-[#838900]'
          : index === 3
            ? 'text-purple-800'
            : 'text-pink-800'
}
